import React, { useMemo } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { type Change } from '@/shared/types/parse-diff';
import { cn } from '@/lib/utils';
import { useTheme } from '@/app/providers/ThemeProvider';

interface LineProps {
  change: Change;
  language: string;
}

const Line: React.FC<LineProps> = ({ change, language }) => {
  const { theme } = useTheme();

  const isDarkMode = useMemo(() => {
    if (theme === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return theme === 'dark';
  }, [theme]);



  const { type, content } = change;

  const prefix = content.substring(0, 1);
  const lineContent = content.substring(1).trimEnd();

  let oldLine: number | undefined;
  let newLine: number | undefined;

  if (change.type === 'normal') {
    oldLine = change.ln1;
    newLine = change.ln2;
  } else if (change.type === 'add') {
    newLine = change.ln;
  } else if (change.type === 'del') {
    oldLine = change.ln;
  }

  return (
    <div
      className={cn(
        'flex font-mono bg-background text-[0.675rem] leading-[1rem] transition-colors duration-200 hover:bg-muted',
        type === 'add' && 'bg-green-500/20',
        type === 'del' && 'bg-red-500/20'
      )}
    >
      <div className="w-8 text-right text-muted-foreground pr-2 select-none">
        {oldLine}
      </div>
      <div className="w-8 text-right text-muted-foreground pr-4 select-none">
        {newLine}
      </div>
      <div className="flex-1 flex">
        <span className="w-4 select-none text-muted-foreground">{prefix}</span>
        <SyntaxHighlighter
          language={language}
          style={isDarkMode ? oneDark : oneLight}
          customStyle={{
            background: 'transparent',
            padding: '0',
            margin: '0',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
            flex: 1,
          }}
          codeTagProps={{
            style: { fontFamily: 'inherit', fontSize: 'inherit', border: 'none'},
          }}
        >
          {lineContent}
        </SyntaxHighlighter>
      </div>
    </div>
  );
};

export default React.memo(Line);
